import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"

import Toast from "@/components/ui/toast"
import { useUploadFileMutation } from "@/store/FileAPI"

interface UseDragAndDropOptions {
  onFileSelect: (fileUrl: string) => void
  acceptedTypes?: string[]
  uploadType?: string
}

interface UseDragAndDropReturn {
  fileName: string
  isDragOver: boolean
  isUploading: boolean
  handleDragOver: (e: React.DragEvent) => void
  handleDragLeave: (e: React.DragEvent) => void
  handleDrop: (e: React.DragEvent) => Promise<void>
  clearFile: () => void
}

const DEFAULT_ACCEPTED_TYPES = ["image/", "video/"]
const DEFAULT_UPLOAD_TYPE = "public"

export const useDragAndDrop = ({
  onFileSelect,
  acceptedTypes = DEFAULT_ACCEPTED_TYPES,
  uploadType = DEFAULT_UPLOAD_TYPE,
}: UseDragAndDropOptions): UseDragAndDropReturn => {
  const { t } = useTranslation()
  const [fileName, setFileName] = useState<string>("")
  const [isDragOver, setIsDragOver] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [upload] = useUploadFileMutation()

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragOver(false)

      const files = Array.from(e.dataTransfer.files)
      if (files.length === 0) return

      const file = files[0]

      setFileName(file.name)

      // Validate file type
      const isValidType = acceptedTypes.some((type) => file.type.startsWith(type))

      if (!isValidType) {
        Toast({ type: "error", message: t("invalidFileType") })
        return
      }

      setIsUploading(true)

      try {
        const formData = new FormData()
        formData.append("files", file)

        const uploadRequest = {
          files: formData,
          type: uploadType,
        }

        const response = await upload(uploadRequest).unwrap()
        const fileUrl = response.files[0]?.location ?? ""

        onFileSelect(fileUrl)
      } catch (error) {
        console.error("Error uploading file:", error)
        Toast({ type: "error", message: t("campaignManager.booking.content.upload.uploadFailed") })
      } finally {
        setIsUploading(false)
      }
    },
    [upload, onFileSelect, acceptedTypes, uploadType, t],
  )

  const clearFile = useCallback(() => {
    setFileName("")
    setIsUploading(false)
  }, [])

  return {
    fileName,
    isDragOver,
    isUploading,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    clearFile,
  }
}
